/**
 * Projectile system for managing bet projectiles
 */

import { Projectile } from '../types';
import { ANIMATION_CONFIG } from '../constants';
import { easeOutCubic } from '../utils/canvasUtils';

export class ProjectileSystem {
  private projectiles: Projectile[] = [];
  private isMobile: boolean = false;
  
  constructor(isMobile: boolean = false) {
    this.isMobile = isMobile;
  }

  /**
   * Adds a new projectile
   */
  addProjectile(params: {
    startX: number;
    startY: number;
    targetX: number;
    targetY: number;
    size: number;
    color: string;
    trailColor: string;
    isUserBet: boolean;
    speed: number;
    amount: number;
  }): void {
    this.projectiles.push({
      x: params.startX,
      y: params.startY,
      targetX: params.targetX,
      targetY: params.targetY,
      startX: params.startX,
      startY: params.startY,
      progress: 0,
      size: params.size,
      color: params.color,
      trailColor: params.trailColor,
      isUserBet: params.isUserBet,
      speed: params.speed,
      amount: params.amount,
    });
  }

  /**
   * Updates and draws all projectiles
   * Returns array of impact positions for effects
   */
  updateAndDraw(
    ctx: CanvasRenderingContext2D,
    width: number,
    ghostLinePoints: number[],
    onImpact: (x: number, y: number, force: number, side: 'left' | 'right', amount: number) => void
  ): void {
    this.projectiles = this.projectiles.filter((projectile) => {
      // Update progress
      projectile.progress += projectile.speed;

      // Calculate current position with easing
      const easedProgress = easeOutCubic(projectile.progress);
      projectile.x = projectile.startX + (projectile.targetX - projectile.startX) * easedProgress;
      projectile.y = projectile.startY + (projectile.targetY - projectile.startY) * easedProgress;

      // Check if reached target
      const hasReachedTarget =
        projectile.startX < width / 2
          ? projectile.x >= projectile.targetX // Left side projectile
          : projectile.x <= projectile.targetX; // Right side projectile

      if (hasReachedTarget || projectile.progress >= 1) {
        // Trigger impact
        const side = projectile.startX < width / 2 ? 'left' : 'right';
        const force = (projectile.size / 8) * ANIMATION_CONFIG.GHOST_LINE_PUSH_FORCE_MULTIPLIER;
        onImpact(projectile.x, projectile.y, force, side, projectile.amount);
        return false;
      }

      // Draw projectile
      this.drawProjectile(ctx, projectile);
      return true;
    });
  }

  /**
   * Draws a single projectile with trail
   */
  private drawProjectile(ctx: CanvasRenderingContext2D, projectile: Projectile): void {
    ctx.save();
    
    if (this.isMobile) {
      // Simplified rendering for mobile - just the projectile, no trail
      ctx.globalAlpha = 1;
      ctx.fillStyle = projectile.color;
      ctx.beginPath();
      ctx.arc(projectile.x, projectile.y, projectile.size, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // Full trail effect for desktop
      ctx.globalCompositeOperation = "lighter";

      // Draw simplified trail (reduced segments)
      const trailSegments = Math.min(3, ANIMATION_CONFIG.PROJECTILE_TRAIL_LENGTH);
      for (let i = 0; i < trailSegments; i++) {
        const trailProgress = Math.max(0, projectile.progress - i * 0.03);
        const trailX = projectile.startX + (projectile.targetX - projectile.startX) * trailProgress;
        const trailY = projectile.startY + (projectile.targetY - projectile.startY) * trailProgress;

        ctx.globalAlpha = (1 - i / trailSegments) * 0.4;
        ctx.fillStyle = projectile.trailColor;
        ctx.beginPath();
        ctx.arc(
          trailX,
          trailY,
          projectile.size * (1 - i / trailSegments),
          0,
          Math.PI * 2
        );
        ctx.fill();
      }

      // Draw main projectile
      ctx.globalAlpha = 1;
      ctx.fillStyle = projectile.color;
      ctx.beginPath();
      ctx.arc(projectile.x, projectile.y, projectile.size, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  /**
   * Clears all projectiles
   */
  clear(): void {
    this.projectiles = [];
  }
}